import styled from "@emotion/styled";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DashboardIcon from "@mui/icons-material/Dashboard";
import InventoryIcon from "@mui/icons-material/Inventory";
import MessageIcon from "@mui/icons-material/Message";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import GCMELogo from "assets/images/gcme-logo.png";
import YotorLogo from "assets/images/logo.png";
import { Notification } from "components/UI";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import { useMembers, useMembersStore } from "features/members";
import i18next from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useAuthUser } from "react-auth-kit";
import { useTranslation } from "react-i18next";
import {
  BsFacebook,
  BsShareFill,
  BsTelegram,
  BsWhatsapp
} from "react-icons/bs";
import { Link, useNavigate } from "react-router-dom";
import {
  FacebookShareButton,
  TelegramShareButton,
  WhatsappShareButton
} from "react-share";
import { useDebounce } from "react-use";
import { useCentralStore } from "stores";
import { useDailyVerse } from "../api";
import { ComingEventPanel, TasksPanel, TodayEventPanel } from "../components";

const QuickActionButton = styled(Button)(({ theme }) => ({
  padding: "12px 16px",
  borderRadius: "8px",
  justifyContent: "flex-start",
  color: theme.palette.primary.main,
  backgroundColor: "#fff",
  fontSize: "0.875rem",
  "&:hover": {
    color: theme.palette.primary.main,
    backgroundColor: "#fff"
  },
  [theme.breakpoints.up("sm")]: {
    padding: "12px 24px",
    fontSize: "0.9375rem"
  }
}));

export const QuickBoard = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [textToShare, setTextToShare] = useState("");

  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const setHeaderTitle = useCentralStore(state => state.setHeaderTitle);
  const queries = useMembersStore(state => state.queries);
  const setQueries = useMembersStore(state => state.setQueries);

  const auth = useAuthUser();
  const currentDate = new Date();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useDebounce(
    () => {
      setDebouncedValue(searchTerm);
    },
    500,
    [searchTerm]
  );

  useEffect(() => {
    setHeaderTitle(t("quickboard.title"));

    return () => {
      setHeaderTitle("");
    };
  }, [setHeaderTitle, i18next.language]);

  useEffect(() => {
    if (debouncedValue) {
      setQueries({
        search: debouncedValue
      });
    }
  }, [debouncedValue]);

  const redirectToFinance = originalHref => {
    return `/finances?redirectTo=${encodeURIComponent(originalHref)}`;
  };

  const quickActions = [
    {
      icon: <DashboardIcon />,
      label: t("quickboard.openDashboard"),
      href: "/"
    },
    {
      icon: <PersonAddIcon />,
      label: t("quickboard.registerMember"),
      href: "/members/add"
    },
    {
      icon: <MessageIcon />,
      label: t("quickboard.sendMessage"),
      href: "/messages/new"
    },
    {
      icon: <AttachMoneyIcon />,
      label: t("quickboard.recordRevenue"),
      href: redirectToFinance("/finances/temp/revenue#record-revenue")
    },
    {
      icon: <MoneyOffIcon />,
      label: t("quickboard.requestRequisition"),
      href: redirectToFinance("/finances/temp/expense/requisition/request")
    },
    {
      icon: <PersonIcon />,
      label: t("quickboard.addNewStaff"),
      href: "/hr#add-employee"
    },
    {
      icon: <InventoryIcon />,
      label: t("quickboard.addNewAsset"),
      href: "/assets#register-asset"
    }
  ];

  const { data: members } = useMembers({
    churchId: selectedChurch?.id,
    queries: queries,
    config: {
      enabled: !!debouncedValue
    }
  });

  const { data: dailyVerse, isLoading: isDailyVerseLoading } = useDailyVerse();

  const encodedBookName = dailyVerse?.book
    ? dailyVerse?.book.trim().replace(/\s+/g, "%2520")
    : "";
  const chapter = dailyVerse?.chapter ? String(dailyVerse?.chapter).trim() : "";
  const SHARE_URL = `https://amharic-bible.pages.dev/#/reading/${encodedBookName}/${chapter}`;

  const greeting = useMemo(() => {
    const hour = currentDate.getHours();
    if (hour < 12) return t("quickboard.goodMorning");
    if (hour < 18) return t("quickboard.goodAfternoon");
    return t("quickboard.goodEvening");
  }, [currentDate]);

  return (
    <Stack
      sx={{
        p: { xs: 2, sm: 3 },
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Notification />

      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        mb={{ xs: 3, md: 4 }}
        spacing={{ xs: 2, md: 0 }}
      >
        <Stack>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: "#fff",
              textTransform: "uppercase",
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" }
            }}
          >
            {greeting}, {auth()?.first_name} {auth()?.last_name}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: "#fff",
              textTransform: "capitalize",
              fontSize: { xs: "0.875rem", sm: "1rem" }
            }}
          >
            {selectedChurch?.name}
          </Typography>
        </Stack>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "baseline" }}
          gap={{ xs: 1, sm: 2 }}
        >
          <Typography
            variant="h2"
            sx={{
              color: "#fff",
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" }
            }}
          >
            {currentDate.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true
            })}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: "#fff",
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }
            }}
          >
            {new Intl.DateTimeFormat("am-ET", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
              calendar: "ethiopic"
            }).format(currentDate)}
          </Typography>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "300px minmax(0, 1fr)",
            lg: "300px minmax(0, 1fr) 300px"
          },
          gap: { xs: 2, sm: 3 },
          flex: { xs: "0 1 auto", lg: 1 },
          minHeight: 0
        }}
      >
        <Stack spacing={{ xs: 2, sm: 3 }} sx={{ order: { xs: 2, md: 1 } }}>
          {/* Quick Actions */}
          <Stack
            sx={{
              border: "1px solid rgba(255, 255, 255, 0.5)",
              backgroundColor: "rgba(0, 0, 0, 0.1)",
              backdropFilter: "blur(8px)",
              p: { xs: 1.5, sm: 2 },
              borderRadius: 6
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                mb: 1,
                color: "#fff",
                fontWeight: 700,
                fontSize: { xs: "0.95rem", sm: "1rem" }
              }}
            >
              {t("quickboard.quickActions")}
            </Typography>
            <Stack spacing={{ xs: 1.5, sm: 2 }}>
              {quickActions.map((action, index) => (
                <QuickActionButton
                  fullWidth
                  key={index}
                  startIcon={action.icon}
                  variant="contained"
                  component={Link}
                  to={action.href}
                >
                  {action.label}
                </QuickActionButton>
              ))}
            </Stack>
          </Stack>

          {/* Today's Tasks */}
          <TasksPanel />
        </Stack>

        <Stack
          sx={{
            width: { xs: "100%", md: "85%", lg: "70%" },
            mx: "auto",
            order: { xs: 1, md: 2 }
          }}
        >
          {/* Daily Text */}
          <Stack
            sx={{
              border: "1px solid rgba(255, 255, 255, 0.5)",
              backgroundColor: "rgba(0, 0, 0, 0.1)",
              backdropFilter: "blur(8px)",
              p: { xs: 1.5, sm: 2 },
              borderRadius: 6
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#fff",
                fontSize: { xs: "1rem", sm: "1.25rem" }
              }}
            >
              የዕለቱ ጥቅስ
            </Typography>
            <Divider sx={{ my: 2, backgroundColor: "#fff" }} />
            {isDailyVerseLoading ? (
              <Skeleton variant="rounded" height={60} />
            ) : (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#fff",
                    fontWeight: 900,
                    fontSize: { xs: "1rem", sm: "1.25rem" }
                  }}
                >
                  {dailyVerse?.text}
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#fff",
                      fontSize: { xs: "0.75rem", sm: "0.875rem" }
                    }}
                  >
                    {dailyVerse?.citation}
                  </Typography>
                  {!showShareOptions && (
                    <IconButton
                      onClick={() => {
                        setTextToShare(
                          `${dailyVerse?.text} -${dailyVerse?.citation}`
                        );
                        setShowShareOptions(!showShareOptions);
                      }}
                      sx={{ p: 0 }}
                    >
                      <BsShareFill color="#fff" size={16} />
                    </IconButton>
                  )}
                  {showShareOptions && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <FacebookShareButton title={textToShare} url={SHARE_URL}>
                        <BsFacebook color="#fff" />
                      </FacebookShareButton>
                      <TelegramShareButton title={textToShare} url={SHARE_URL}>
                        <BsTelegram color="#fff" />
                      </TelegramShareButton>
                      <WhatsappShareButton title={textToShare} url={SHARE_URL}>
                        <BsWhatsapp color="#fff" />
                      </WhatsappShareButton>
                    </Stack>
                  )}
                </Stack>
              </>
            )}
          </Stack>

          {/* Search Bar */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            mt={{ xs: 3, sm: 4 }}
          >
            <Box sx={{ flex: 1, position: "relative" }}>
              <TextField
                fullWidth
                placeholder={t("quickboard.searchMember")}
                variant="outlined"
                onChange={e => setSearchTerm(e.target.value)}
                sx={{
                  outline: "none",
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                  backdropFilter: "blur(8px)",
                  borderRadius: "12px",
                  "& .MuiInputBase-input": {
                    color: "#fff"
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#fff"
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.5)"
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.5)"
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.5)"
                    }
                  }
                }}
              />
              {members?.result?.length === 0 && debouncedValue && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    mt: 1,
                    backgroundColor: "white",
                    backdropFilter: "blur(8px)",
                    borderRadius: "12px",
                    border: "1px solid rgba(255, 255, 255, 0.5)",
                    p: 2,
                    zIndex: 1
                  }}
                >
                  <Typography
                    sx={{ color: "text.secondary", textAlign: "center" }}
                  >
                    {t("quickboard.noResultsFound")}
                  </Typography>
                </Box>
              )}
              {!members && debouncedValue && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    mt: 1,
                    backgroundColor: "white",
                    backdropFilter: "blur(8px)",
                    borderRadius: "12px",
                    border: "1px solid rgba(255, 255, 255, 0.5)",
                    p: 2,
                    zIndex: 1
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <CircularProgress size={20} />
                    <Typography sx={{ color: "text.secondary" }}>
                      {t("quickboard.searching")}
                    </Typography>
                  </Stack>
                </Box>
              )}
              {members?.result?.length > 0 && debouncedValue && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    mt: 1,
                    backgroundColor: "white",
                    backdropFilter: "blur(8px)",
                    borderRadius: "12px",
                    border: "1px solid rgba(255, 255, 255, 0.5)",
                    maxHeight: "300px",
                    overflowY: "auto",
                    overflowX: "hidden",
                    zIndex: 1,
                    "&::-webkit-scrollbar": {
                      width: "6px",
                      display: "block"
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "rgba(0, 0, 0, 0.1)",
                      margin: "8px 0",
                      borderRadius: "3px"
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "rgba(0, 0, 0, 0.2)",
                      borderRadius: "3px"
                    }
                  }}
                >
                  {members.result.map(member => (
                    <Stack
                      key={member.id}
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{
                        p: 2,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)"
                        }
                      }}
                      onClick={() => {
                        setSearchTerm("");
                        setDebouncedValue("");
                        navigate(`/members/${member.id}`);
                      }}
                    >
                      <Avatar
                        src={`${IMAGE_URL}/${member.profile_image_url}`}
                        alt={member.first_name}
                      >
                        {member.first_name?.[0]}
                      </Avatar>
                      <Stack>
                        <Typography>
                          {member.first_name} {member.father_name}{" "}
                          {member.last_name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {member.phone}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Box>
              )}
            </Box>

            <Button
              variant="contained"
              endIcon={<SearchIcon />}
              onClick={() => setDebouncedValue(searchTerm)}
              fullWidth
              sx={{
                borderRadius: "12px",
                padding: { xs: "12px 24px", sm: "16px 32px" },
                width: { sm: "auto" },
                "&:hover": {
                  backgroundColor: "primary.main"
                }
              }}
            >
              {t("quickboard.search")}
            </Button>
          </Stack>
        </Stack>

        {/* Today's Events */}
        <Stack
          spacing={{ xs: 2, sm: 3 }}
          sx={{
            order: { xs: 3, lg: 3 },
            display: { xs: "flex-column", lg: "flex" }
          }}
        >
          <TodayEventPanel />

          <ComingEventPanel />
        </Stack>
      </Box>

      {/* Powered By Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems="center"
        justifyContent="center"
        mt={{ xs: 3, sm: 4 }}
        spacing={2}
      >
        <Typography
          variant="caption"
          sx={{
            color: "#fff",
            fontSize: { xs: "0.7rem", sm: "0.75rem" }
          }}
        >
          {t("quickboard.poweredBy")}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <img
            src={YotorLogo}
            alt="Yotor logo"
            height={24}
            style={{ height: "24px" }}
          />
          <img
            src={GCMELogo}
            alt="GCME logo"
            height={24}
            style={{ height: "24px" }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
